import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './DesktopFilters.styles';
import { formatBooleanValue } from './DesktopFilters.helpers';
import { DesktopFilterAccordion } from './DesktopFilterAccordion';
import { Property } from '@/api/types/categories.types';
import { BooleanCollection } from '@/components/Filters/FiltersDrawer.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';

interface Props {
  property: Property;
  collection: BooleanCollection;
}

const options = [
  { label: 'common.no_matter', value: undefined },
  { label: 'common.yes', value: true },
  { label: 'common.no', value: false },
];

export const BooleanDesktopFilter: FC<Props> = ({ property, collection }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const accordionRef = useRef<AccordionRef>(null);

  const { legacyResourceId, name, filterOpenByDefault } = property;
  const currentValue = collection[legacyResourceId];
  const value = formatBooleanValue(currentValue, t);
  const shouldOpenByDefault = !!filterOpenByDefault && !value;

  function getHref(val: boolean | undefined) {
    const searchParams = new URLSearchParams(queryString);
    const key = `p[${legacyResourceId}]`;

    // `user_id` is preserved so desktop filters keep the user ads page scoped.
    searchParams.set(QUERY.keywordsSource, KeywordsSource.Typewritten);

    if (val !== undefined) {
      searchParams.set(key, String(val));
    } else {
      searchParams.delete(key);
    }

    return `${clearUrl}?${searchParams.toString()}`;
  }

  function handleClear() {
    const href = getHref(undefined);
    router.push(href, undefined, { scroll: false });
  }

  return (
    <DesktopFilterAccordion
      key={`${legacyResourceId}-${String(shouldOpenByDefault)}`}
      ref={accordionRef}
      title={name}
      value={value}
      onClear={handleClear}
      dataTestId={`boolean-property-${legacyResourceId}`}
      defaultOpen={shouldOpenByDefault}
    >
      <S.BooleanOptionsContainer>
        {options.map(option => {
          const isSelected = currentValue === option.value;
          return (
            <S.Chip
              key={option.label}
              appearance={isSelected ? 'primary' : 'gray'}
              isRounded
              href={getHref(option.value)}
              onClick={() => accordionRef.current?.close()}
              dataTestId={SearchResultsPageTestIds.desktopFilters.booleanOption}
            >
              {t(option.label)}
            </S.Chip>
          );
        })}
      </S.BooleanOptionsContainer>
    </DesktopFilterAccordion>
  );
};
